import { IntentEditor } from "@/components/common/IntentEditor";
import { JiraIntegrationCard } from "@/components/settings/JiraIntegrationCard";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	useOrg,
	useOrgIntentMutation,
	useSlackConnection,
	useSlackMutations,
	useUpdateSettings,
} from "@/hooks";
import { canCarryText, parseAccent } from "@/lib/brand";
import { ApiError } from "@/services";
import type { OrgIntent } from "@/types/api";
import { AlertTriangle, X } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Branding and how people join. Commercial terms — plan, price, seat count,
 * contract dates — are NOT here: they live on the staff plane, and the Gateway
 * rejects them from this endpoint regardless of what the UI sends.
 */
export function SettingsPage() {
	const org = useOrg();
	const update = useUpdateSettings();

	const [logoUrl, setLogoUrl] = useState("");
	const [accent, setAccent] = useState("");
	const [domains, setDomains] = useState<string[]>([]);
	const [newDomain, setNewDomain] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);

	useEffect(() => {
		if (!org.data) return;
		setLogoUrl(org.data.org.settings.logo_url ?? "");
		setAccent(org.data.org.settings.accent ?? "");
		setDomains(org.data.org.auth_policy.auto_join_domains ?? []);
	}, [org.data]);

	const parsedAccent = parseAccent(accent);

	if (org.isLoading) return <Skeleton className="h-96 w-full" />;

	const seats = org.data?.seats;

	async function save() {
		setError(null);
		setSaved(false);
		try {
			await update.mutateAsync({
				settings: {
					logo_url: logoUrl || undefined,
					accent: accent || undefined,
				},
				autoJoinDomains: domains,
			});
			setSaved(true);
		} catch (e) {
			setError(e instanceof ApiError ? e.message : "Could not save settings.");
		}
	}

	function addDomain() {
		const d = newDomain.trim().toLowerCase().replace(/^@/, "");
		if (!d || domains.includes(d)) return;
		setDomains([...domains, d]);
		setNewDomain("");
	}

	return (
		<>
			<PageHeader
				title="Settings"
				description="Branding, how people join, and what your organization is working towards."
			/>

			<OrgIntentCard />
			<SlackIntegrationCard />
			<JiraIntegrationCard />

			<Card className="mb-6">
				<CardContent className="flex flex-col gap-4 p-5">
					<h2 className="text-body font-semibold text-heading">Branding</h2>
					<Field label="Logo URL">
						<Input
							value={logoUrl}
							onChange={(e) => setLogoUrl(e.target.value)}
							placeholder="https://…"
						/>
					</Field>
					<Field label="Accent colour">
						<div className="flex items-center gap-2">
							<Input
								value={accent}
								onChange={(e) => setAccent(e.target.value)}
								placeholder="#ec7723"
							/>
							{parsedAccent ? (
								<span
									className="size-8 shrink-0 rounded-md border border-divider"
									style={{ background: parsedAccent }}
								/>
							) : null}
						</div>
						{/* Say what is wrong at the moment it is wrong, rather than
						    saving something that silently does nothing. */}
						{accent.trim() && !parsedAccent ? (
							<span className="text-caption text-error">
								Use a hex colour, like #ec7723.
							</span>
						) : null}
						{parsedAccent && !canCarryText(parsedAccent) ? (
							<span className="text-caption text-muted-foreground">
								This colour is too mid-toned to put text on, so it's used as an
								accent only — you'll see it as the bar across the top.
							</span>
						) : null}
					</Field>
				</CardContent>
			</Card>

			<Card className="mb-6">
				<CardContent className="flex flex-col gap-4 p-5">
					<div>
						<h2 className="text-body font-semibold text-heading">
							Automatic joining
						</h2>
						<p className="mt-1 text-caption text-muted-foreground">
							Anyone signing in with one of these email domains joins your
							organization automatically.
						</p>
					</div>

					{/* Adding a domain silently starts spending seats. Say so here,
					    with the real number, rather than letting them find out.
					    On an unlimited plan there is no number and no warning to
					    give — `available` is null there, and rendering it yields
					    "You have  seats available." */}
					{domains.length > 0 && seats?.available != null ? (
						<div className="flex items-start gap-2 rounded-md border border-orange-stroke bg-orange-surface px-3 py-2.5">
							<AlertTriangle className="mt-0.5 size-4 shrink-0 text-orange-text" />
							<p className="text-caption text-orange-text">
								Everyone with a matching address takes a paid seat when they
								sign in. You have{" "}
								<strong className="tabular">{seats.available}</strong>{" "}
								{seats.available === 1 ? "seat" : "seats"} available.
							</p>
						</div>
					) : null}

					<div className="flex flex-wrap gap-2">
						{domains.map((d) => (
							<span
								key={d}
								className="inline-flex items-center gap-1.5 rounded-full border border-divider bg-surface3 px-2.5 py-1 text-caption text-paragraph"
							>
								@{d}
								<button
									type="button"
									onClick={() => setDomains(domains.filter((x) => x !== d))}
									className="text-muted1 transition-colors hover:text-error"
									aria-label={`Remove ${d}`}
								>
									<X className="size-3" />
								</button>
							</span>
						))}
					</div>

					<div className="flex gap-2">
						<Input
							value={newDomain}
							onChange={(e) => setNewDomain(e.target.value)}
							onKeyDown={(e) => e.key === "Enter" && addDomain()}
							placeholder="example.com"
						/>
						<Button
							variant="outline"
							onClick={addDomain}
							disabled={!newDomain.trim()}
						>
							Add
						</Button>
					</div>
				</CardContent>
			</Card>

			<div className="flex items-center gap-3">
				<Button onClick={save} disabled={update.isPending}>
					{update.isPending ? "Saving…" : "Save changes"}
				</Button>
				{saved ? (
					<span className="text-caption text-success">Saved.</span>
				) : null}
				{error ? (
					<span className="text-caption text-error">{error}</span>
				) : null}
			</div>
		</>
	);
}

function SlackIntegrationCard() {
	const org = useOrg();
	const connection = useSlackConnection();
	const { connect, disconnect } = useSlackMutations();
	const [error, setError] = useState<string | null>(null);
	const canEdit = org.data?.org.role !== "member";

	async function start() {
		setError(null);
		try {
			const result = await connect.mutateAsync();
			window.location.assign(result.url);
		} catch (e) {
			setError(
				e instanceof ApiError ? e.message : "Could not start Slack setup.",
			);
		}
	}

	async function remove() {
		if (
			!window.confirm("Disconnect this Slack workspace from your organization?")
		)
			return;
		setError(null);
		try {
			await disconnect.mutateAsync();
		} catch (e) {
			setError(
				e instanceof ApiError ? e.message : "Could not disconnect Slack.",
			);
		}
	}

	return (
		<Card className="mb-6">
			<CardContent className="flex flex-col gap-4 p-5">
				<div>
					<h2 className="text-body font-semibold text-heading">Slack</h2>
					<p className="mt-1 text-caption text-muted-foreground">
						Bring Curo plans, learning questions, and task actions into your
						organization's Slack workspace.
					</p>
				</div>

				{connection.isLoading ? (
					<Skeleton className="h-10 w-full" />
				) : connection.data?.connected ? (
					<div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-divider bg-surface3 p-3">
						<div>
							<p className="text-caption font-medium text-heading">Connected</p>
							<p className="text-caption text-muted-foreground">
								{connection.data.teamName ||
									connection.data.teamId ||
									"Slack workspace"}
							</p>
						</div>
						{canEdit ? (
							<Button
								variant="outline"
								onClick={remove}
								disabled={disconnect.isPending}
							>
								{disconnect.isPending ? "Disconnecting…" : "Disconnect"}
							</Button>
						) : null}
					</div>
				) : canEdit ? (
					<Button
						className="self-start"
						onClick={start}
						disabled={connect.isPending}
					>
						{connect.isPending ? "Opening Slack…" : "Connect Slack"}
					</Button>
				) : (
					<p className="text-caption text-muted-foreground">
						Ask an organization admin to connect Slack.
					</p>
				)}

				{connection.isError ? (
					<p className="text-caption text-error">
						Could not load Slack status.
					</p>
				) : null}
				{error ? <p className="text-caption text-error">{error}</p> : null}
			</CardContent>
		</Card>
	);
}

function Field({
	label,
	children,
}: { label: string; children: React.ReactNode }) {
	return (
		<label className="flex flex-col gap-1.5">
			<span className="text-caption font-medium text-paragraph">{label}</span>
			{children}
		</label>
	);
}

/**
 * The organization's own stated intent, layered under its teams'.
 *
 * Separate card and separate save from Branding: they go to different endpoints
 * and fail independently, and one Save button that half-worked would be worse
 * than two that each say what they did.
 *
 * As on the Teams screen, nothing reads this yet (CUR-126) and the description
 * says so rather than implying a change nobody would see.
 */
function OrgIntentCard() {
	const org = useOrg();
	const save = useOrgIntentMutation();
	const [intent, setIntent] = useState<OrgIntent>({});
	const [error, setError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);

	// Seeded from the server, which NORMALISES what it stores — so this shows
	// what is actually recorded rather than what was last typed here.
	useEffect(() => {
		if (org.data) setIntent(org.data.org.intent ?? {});
	}, [org.data]);

	const canEdit = org.data?.org.role !== "member";

	return (
		<Card className="mb-6">
			<CardContent className="flex flex-col gap-4 p-5">
				<div>
					<h2 className="text-body font-semibold text-heading">
						What your organization is working towards
					</h2>
					<p className="mt-1 text-caption text-muted-foreground">
						A starting point for every team. Stored for planning — it does not
						change anyone's feed today.
					</p>
				</div>

				<IntentEditor
					value={intent}
					onChange={(next) => {
						setIntent(next);
						setSaved(false);
					}}
					disabled={!canEdit || save.isPending}
				/>

				{error ? <p className="text-caption text-error">{error}</p> : null}

				{canEdit ? (
					<div className="flex items-center gap-3">
						<Button
							onClick={() => {
								setError(null);
								setSaved(false);
								save.mutate(intent, {
									onSuccess: () => setSaved(true),
									onError: (err) =>
										setError(
											err instanceof ApiError
												? err.message
												: "Something went wrong saving.",
										),
								});
							}}
							disabled={save.isPending}
						>
							{save.isPending ? "Saving…" : "Save"}
						</Button>
						{saved ? (
							<span className="text-caption text-muted-foreground">Saved.</span>
						) : null}
					</div>
				) : null}
			</CardContent>
		</Card>
	);
}
